import {
  Calendar,
  convertTasksToCalendarEvents,
  fullDayMonthDateString,
  monthDateString,
  Task,
  TaskOccurrence,
  toDateString,
} from '@nextforce/api'
import { Icon, makeStyles, Text } from '@rneui/themed'
import { t } from 'app/i18l'
import moment from 'moment'
import { useCallback, useMemo, useState } from 'react'
import { SectionList, TouchableOpacity, View } from 'react-native'
import { SECTION_HEADER_HEIGHT } from 'schedule/utils/schedule'
import { TaskEventCard } from 'schedule/views/event/task'
import { EventSkeleton } from 'schedule/views/schedule/skeleton'
import {
  Dropdown,
  DropdownItemComponentProps,
  DropdownSection,
} from 'shared/components/dropdown'
import { FloatingActionButton } from 'shared/components/fab'
import { generateUniqueId } from 'shared/utils'
import { getTaskStatus, TaskStatus } from 'tasks/utils'

type TaskItemData = { task: Task; occurrence: TaskOccurrence }
type EmptyItemData = { isEmpty: true }
type TaskSectionItem = TaskItemData | EmptyItemData

type TaskSection = {
  title: string
  date: string
  data: TaskSectionItem[]
}

const FILTER_DROPDOWN_SECTIONS: DropdownSection[] = [
  { data: [{ id: 'all', name: t('all').toUpperCase() }] },
  { data: [{ id: 'due', name: t('due').toUpperCase() }] },
  { data: [{ id: 'open', name: t('open').toUpperCase() }] },
  { data: [{ id: 'late', name: t('late').toUpperCase() }] },
  { data: [{ id: 'done', name: t('done').toUpperCase() }] },
]

interface Props {
  tasks: Task[] | undefined
  startDate: string
  endDate: string
  calendar: Calendar
  onPressTask: (task: Task) => void
  loading: boolean
  onChangeWeek: (direction: 'next' | 'prev') => void
  onAddTask: () => void
}

export function TasksView({
  tasks,
  startDate,
  endDate,
  calendar,
  onPressTask,
  onChangeWeek,
  loading,
  onAddTask,
}: Props) {
  const [filterType, setFilterType] = useState<TaskStatus | 'all'>('all')
  const styles = useStyles()
  const taskCalendarEvents = useMemo(() => {
    return convertTasksToCalendarEvents(
      tasks,
      startDate,
      calendar,
      generateUniqueId
    )
  }, [tasks, startDate, calendar])

  // Transform taskCalendarEvents to SectionList format
  const taskSections = useMemo(() => {
    const sectionsMap: Record<string, TaskSection> = {}
    const currentDate = moment(startDate)
    const end = moment(endDate)

    while (currentDate.isSameOrBefore(end, 'day')) {
      const date = toDateString(currentDate)
      sectionsMap[date] = {
        title: fullDayMonthDateString(date),
        date,
        data: [{ isEmpty: true } as TaskSectionItem],
      }
      currentDate.add(1, 'day')
    }

    if (taskCalendarEvents) {
      Object.entries(taskCalendarEvents).forEach(([date, events]) => {
        let tasksForDay = events.flatMap(event =>
          event.tasks.flatMap(task =>
            task.occurrences.map(
              occurrence =>
                ({
                  task,
                  occurrence,
                } as TaskSectionItem)
            )
          )
        )

        switch (filterType) {
          case 'due':
            tasksForDay = tasksForDay.filter(
              item => 'task' in item && getTaskStatus(item.task) === 'due'
            )
            break
          case 'open':
            tasksForDay = tasksForDay.filter(
              item => 'task' in item && getTaskStatus(item.task) === 'open'
            )
            break
          case 'late':
            tasksForDay = tasksForDay.filter(
              item => 'task' in item && getTaskStatus(item.task) === 'late'
            )
            break
          case 'done':
            tasksForDay = tasksForDay.filter(
              item => 'task' in item && getTaskStatus(item.task) === 'done'
            )
            break
          case 'all':
            break
        }

        if (tasksForDay.length > 0) {
          sectionsMap[date].data = tasksForDay
        }
      })
    }

    return Object.values(sectionsMap).sort((a, b) =>
      moment(a.date).diff(moment(b.date))
    )
  }, [taskCalendarEvents, filterType, startDate, endDate])

  const renderItem = useCallback(
    ({ item }: { item: TaskSectionItem }) => {
      function isTaskItemData(
        itemToCheck: TaskSectionItem
      ): itemToCheck is TaskItemData {
        return 'task' in itemToCheck && 'occurrence' in itemToCheck
      }

      if ('isEmpty' in item) {
        return (
          <View style={styles.emptyCardContainer}>
            <Text style={styles.emptyCardText}>{t('noTasks')}</Text>
          </View>
        )
      }

      if (isTaskItemData(item)) {
        return (
          <View style={styles.taskEventCardContainer}>
            <TaskEventCard
              task={item.task}
              occurrence={item.occurrence}
              onPressTask={onPressTask}
            />
          </View>
        )
      }

      return null
    },
    [
      onPressTask,
      styles.emptyCardContainer,
      styles.emptyCardText,
      styles.taskEventCardContainer,
    ]
  )

  const renderSectionHeader = useCallback(
    ({ section }: { section: TaskSection }) => (
      <View style={styles.sectionHeaderContainer}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionHeaderText}>{section.title}</Text>
        </View>
      </View>
    ),
    [
      styles.sectionHeader,
      styles.sectionHeaderContainer,
      styles.sectionHeaderText,
    ]
  )

  // Format the date range for display
  const formattedDateRange = useMemo(() => {
    const start = monthDateString(startDate)
    const end = monthDateString(endDate)
    return `${start} - ${end}`
  }, [startDate, endDate])

  // Handlers for week navigation
  const handlePrevWeek = useCallback(() => {
    onChangeWeek?.('prev')
  }, [onChangeWeek])

  const handleNextWeek = useCallback(() => {
    onChangeWeek?.('next')
  }, [onChangeWeek])

  return (
    <View style={styles.container}>
      <View style={styles.controlsContainer}>
        <View style={styles.navigationContainer}>
          <TouchableOpacity onPress={handlePrevWeek} style={styles.navButton}>
            <Icon name="chevron-left" type="material" size={24} />
          </TouchableOpacity>
          <Text style={styles.dateRangeText}>{formattedDateRange}</Text>
          <TouchableOpacity onPress={handleNextWeek} style={styles.navButton}>
            <Icon name="chevron-right" type="material" size={24} />
          </TouchableOpacity>
        </View>
        <View style={styles.filterContainer}>
          <Dropdown
            sections={FILTER_DROPDOWN_SECTIONS}
            selectedId={filterType}
            onSelect={setFilterType}
            buttonStyles={styles.filterButton}
            title={t(filterType)}
            titleStyle={styles.filterButtonText}
            ItemComponent={FilterDropdownItem}
          />
        </View>
      </View>
      {loading ? (
        <EventSkeleton count={7} />
      ) : (
        <>
          <SectionList
            sections={taskSections}
            renderItem={renderItem}
            renderSectionHeader={renderSectionHeader}
            keyExtractor={(item, index) => {
              if ('isEmpty' in item) {
                return `empty-${index}`
              }
              return `${item.task.storeId}-${item.occurrence.taskId}-${index}`
            }}
            stickySectionHeadersEnabled={true}
            contentContainerStyle={styles.contentContainerStyle}
          />
          <FloatingActionButton onPress={onAddTask} />
        </>
      )}
    </View>
  )
}

function FilterDropdownItem({ item, selected }: DropdownItemComponentProps) {
  const styles = useStyles()
  return (
    <View
      style={[
        styles.filterDropdownItem,
        { paddingBottom: item.id === 'done' ? 0 : 15 },
      ]}
    >
      <Text style={styles.filterDropdownItemText}>{item.name}</Text>
      {selected ? <Icon name="check" /> : null}
    </View>
  )
}

const useStyles = makeStyles(theme => ({
  container: {
    flex: 1,
  },
  controlsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  navigationContainer: {
    flex: 2,
    flexDirection: 'row',
    alignItems: 'center',
  },
  navButton: {
    padding: 5,
  },
  dateRangeText: {
    fontSize: 14,
    marginHorizontal: 5,
    fontWeight: '500',
  },
  filterButton: {
    backgroundColor: theme.colors?.grey5,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 4,
  },
  filterText: {
    fontSize: 14,
    marginRight: 2,
    fontWeight: '500',
  },
  sectionHeader: {
    height: SECTION_HEADER_HEIGHT,
    justifyContent: 'center',
    backgroundColor: theme.colors?.grey2,
  },
  sectionHeaderText: {
    color: theme.colors?.white,
    paddingHorizontal: 16,
    fontSize: 14,
  },
  emptyCardContainer: {
    height: 100,
    borderRadius: 5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyCardText: {
    fontSize: 16,
    color: theme.colors?.grey3,
    fontStyle: 'italic',
  },
  sectionHeaderContainer: {
    paddingTop: 5,
    backgroundColor: theme.colors.grey5,
  },
  filterContainer: { flex: 1 },
  filterButtonText: { flex: 1, textAlign: 'left' },
  filterDropdownItem: {
    padding: 0,
    flexDirection: 'row',
    alignItems: 'center',
    width: 100,
  },
  filterDropdownItemText: { flex: 1 },
  contentContainerStyle: {
    flexGrow: 1,
    backgroundColor: theme.colors.grey5,
    paddingBottom: 10,
  },
  taskEventCardContainer: { paddingHorizontal: 10 },
}))
